import { Reducer, useReducer } from 'react';

import isEqualResourceRoleSubject from '@app/features/authorization/utils/isEqualResourceRole';

import { ResourceRole } from '../types';

export function subtract<T>(
  a: T[],
  b: T[],
  identityFn: (a: T, b: T) => boolean = (a, b) => a === b
) {
  return a.filter((r1) => !b.some((r2) => identityFn(r1, r2)));
}

// 이전과 바뀔 resource role을 비교해서 변경분을 반환합니다.
export function diff<T>(
  asIs: ResourceRole<T>[],
  toBe: ResourceRole<T>[]
): ResourceRole<T | null>[] {
  const removedResourceRoles = subtract(asIs, toBe, (a, b) =>
    isEqualResourceRoleSubject(a.subject, b.subject)
  );
  const addedResourceRoles = subtract(
    toBe,
    asIs,
    // resource role의 name만 바뀐 resource role들도 toBe에 포함되어야합니다.
    // 따라서 resource role name까지 같을 때만 toBe에서 제거합니다.
    (a, b) => isEqualResourceRoleSubject(a.subject, b.subject) && a.name === b.name
  );

  return [
    ...removedResourceRoles.map(({ subject }) => ({ subject, name: null })),
    ...addedResourceRoles,
  ];
}

function sort<T>(resourceRoles: ResourceRole<T>[]): ResourceRole<T>[] {
  return [...resourceRoles].sort((a, b) => {
    if (a.subject.subjectType !== b.subject.subjectType) {
      if (a.subject.subjectType === 'GROUP') {
        return -1;
      }
      return 1;
    }

    if (a.subject.subjectId !== b.subject.subjectId) {
      return a.subject.subjectId < b.subject.subjectId ? -1 : 1;
    }

    return 0;
  });
}

type State<T> = {
  initialResourceRoles: ResourceRole<T>[];
  currentResourceRoles: ResourceRole<T>[];
  changes: ResourceRole<T | null>[];
};

type Action<T> =
  | { type: 'INITIALIZE'; initialResourceRoles: ResourceRole<T>[] }
  | { type: 'UPDATE_RESOURCE_ROLES'; resourceRoles: ResourceRole<T>[] }
  | { type: 'REMOVE_RESOURCE_ROLES'; resourceRoles: ResourceRole<T>[] };

function resourceRoleReducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'INITIALIZE': {
      const initialResourceRoles = sort(action.initialResourceRoles);
      return {
        initialResourceRoles,
        currentResourceRoles: initialResourceRoles,
        changes: [],
      };
    }
    case 'UPDATE_RESOURCE_ROLES': {
      // 이미 있던 resource role 업데이트를 위해 중복을 제거하고 추가합니다.
      let nextResourceRole = subtract(state.currentResourceRoles, action.resourceRoles, (a, b) =>
        isEqualResourceRoleSubject(a.subject, b.subject)
      );
      nextResourceRole = nextResourceRole.concat(action.resourceRoles);
      nextResourceRole = sort(nextResourceRole);

      return {
        initialResourceRoles: state.initialResourceRoles,
        currentResourceRoles: nextResourceRole,
        changes: diff(state.initialResourceRoles, nextResourceRole),
      };
    }
    case 'REMOVE_RESOURCE_ROLES': {
      let nextResourceRole = subtract(state.currentResourceRoles, action.resourceRoles, (a, b) =>
        isEqualResourceRoleSubject(a.subject, b.subject)
      );
      nextResourceRole = sort(nextResourceRole);

      return {
        initialResourceRoles: state.initialResourceRoles,
        currentResourceRoles: nextResourceRole,
        changes: diff(state.initialResourceRoles, nextResourceRole),
      };
    }
    default:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      throw new Error(`Not supported action type: ${action.type}`);
  }
}

export default function useResourceRolesDiff<T>() {
  const [state, dispatch] = useReducer<Reducer<State<T>, Action<T>>>(resourceRoleReducer, {
    initialResourceRoles: [],
    currentResourceRoles: [],
    changes: [],
  });
  return {
    currentResourceRoles: state.currentResourceRoles,
    changes: state.changes,
    setInitialResourceRoles: (resourceRoles: ResourceRole<T>[]) =>
      dispatch({ type: 'INITIALIZE', initialResourceRoles: resourceRoles }),
    updateResourceRoles: (resourceRoles: ResourceRole<T>[]) =>
      dispatch({ type: 'UPDATE_RESOURCE_ROLES', resourceRoles }),
    removeResourceRoles: (resourceRoles: ResourceRole<T>[]) =>
      dispatch({ type: 'REMOVE_RESOURCE_ROLES', resourceRoles }),
  };
}
